// Apolo IDE — teamTurnRunner (sprint subagents-team, E3-WS2/WS3).
//
// O ACL entre o Code chat e o Hermes (01-DOCTRINE [T2]): traduz o idioma do
// chat ({ text, route, pushMsg/updateMsg/setLiveStatus }) pro contrato C2 do
// runTeamTurn e de volta. O SessionWorkspace fica com um diff mínimo: chama
// runTeamChatTurn e decide entre "turno tratado" e "segue o loop normal".
//
// UI ao vivo: UM card team_plan (role 'tool') que nasce no team_plan e é
// PATCHado a cada evento (operário mudou de estado, stats no fim) — mesmo
// shape do persistido (C3), então o TeamPlanCard renderiza live e reload sem
// branch. Fases viram o vocabulário do LiveActivity existente (pensando /
// tool 'equipe' / gerando) — zero mudança no LiveActivity.

import { launchTeamTurn } from '../../../lib/runtime/hermesRunWiring.js';

export function routeToBrain(route) {
  return {
    kind: route?.kind === 'local' ? 'local' : 'cloud',
    model: route?.model || undefined,
    baseUrl: route?.baseUrl || undefined,
    adapterId: route?.llmAdapterId || undefined,
    provider: route?.isLmStudio ? 'lm studio' : undefined,
  };
}

/**
 * @param {{text?: string, root?: string, route?: Parameters<typeof routeToBrain>[0],
 * cap?: number, workerModel?: string, readOnly?: boolean, signal?: AbortSignal,
 * pushMsg?: (message: any) => any, updateMsg?: (id: string, patch: any) => void,
 * setLiveStatus?: (status: any) => void, launch?: typeof launchTeamTurn}} [options]
 */
export async function runTeamChatTurn({
  text,
  root,
  route,
  cap,
  workerModel,
  readOnly = false,
  signal,
  pushMsg,
  updateMsg,
  setLiveStatus,
  launch = launchTeamTurn,
} = {}) {
  let cardId = null;
  let cardMeta = null;
  const patchCard = (patch, msgPatch = {}) => {
    if (!cardId) return;
    cardMeta = { ...cardMeta, ...patch };
    updateMsg(cardId, { metadata: cardMeta, ...msgPatch });
  };

  const onEvent = (evt) => {
    if (signal?.aborted) return;
    switch (evt.type) {
      case 'team_phase':
        if (evt.phase === 'planejando') setLiveStatus({ phase: 'pensando' });
        else if (evt.phase === 'executando') setLiveStatus({ phase: 'tool', tool: 'equipe' });
        else if (evt.phase === 'sintetizando') setLiveStatus({ phase: 'gerando' });
        break;
      case 'team_plan': {
        cardMeta = {
          plan: { reasoning: evt.plan.reasoning, subtasks: evt.plan.subtasks },
          children: evt.plan.subtasks.map((s) => ({ title: s.title, state: 'queued' })),
          parentRunId: null,
        };
        cardId = pushMsg({
          role: 'tool',
          tool_name: 'team_plan',
          content: `Plano da equipe — ${evt.plan.subtasks.length} operários`,
          status: 'running',
          metadata: cardMeta,
        });
        break;
      }
      case 'team_started':
        patchCard({ parentRunId: evt.runId ?? null });
        break;
      case 'team_child': {
        const i = evt.child?.index;
        if (!cardMeta || !Number.isInteger(i) || !cardMeta.children?.[i]) break;
        patchCard({
          children: cardMeta.children.map((c, j) => (j === i ? { ...c, state: evt.child.state } : c)),
        });
        break;
      }
      default:
        break; // team_token: síntese chega inteira no tier S
    }
  };

  const out = await launch({ text, root, brain: routeToBrain(route), cap, workerModel, readOnly, signal, onEvent });

  // Líder decidiu solo: o chat segue o loop normal — sem card, sem ruído.
  if (out.mode === 'solo') return { handled: false, reasoning: out.reasoning || '' };

  patchCard(
    {
      children: (out.children || []).map((c) => ({ title: c.title, state: c.state })),
      stats: out.stats || null,
      parentRunId: out.parentRunId ?? cardMeta?.parentRunId ?? null,
    },
    { status: 'done' },
  );

  return buildTurnResult(out, cardMeta);
}

function buildTurnResult(out, cardMeta) {
  if (out.cancelled) {
    return { handled: true, ok: false, stopReason: 'aborted', finalText: out.finalText || '', card: cardMeta };
  }
  if (out.failed) {
    return {
      handled: true,
      ok: false,
      stopReason: 'team_failed',
      error: out.error || 'equipe falhou',
      finalText: out.finalText || '',
      card: cardMeta,
    };
  }
  return { handled: true, ok: true, stopReason: 'team_done', finalText: out.finalText || '', card: cardMeta };
}
